import { ManualService } from './services/manual.services';
import { ManualNavigationService } from './services/manual-navigation.service';
import {
  Component,
  OnInit,
  ViewChild,
  OnDestroy,
  ChangeDetectorRef
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ManualView } from './models/index';
import { BaseServices } from './../kuba.services';
import { NotificationService } from 'src/app/shared/services/notification.service';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { ManualFormComponent } from './components';

@Component({
  templateUrl: 'manual.component.html',
  providers: [],
  standalone: false })
export class ManualComponent implements OnInit, OnDestroy {
  @ViewChild('manualFormInstance') manualFormComponent: ManualFormComponent;
  manualFormShowSaveAndClose = true;
  manualFormHideSaveAndContinue = false;

  isAdminMode: boolean;
  showDialog = false;

  manual: ManualView;
  newManualText = 'NEW_COMMON_MANUAL';
  manualType = 'common';
  manuals: ManualView[];

  manualList: ManualView[];
  portalManualList: ManualView[];
  businessManualList: ManualView[];
  allManualList: ManualView[];

  private subscriptions: Subscription[] = [];

  /**
   * Initializes services and sets admin mode and initial manual list from route data.
   * @param router - The Angular Router service.
   * @param route - The Angular ActivatedRoute service.
   * @param manualService - Service for manual-related operations.
   */
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private manualService: ManualService,
    private messageService: NotificationService,
    private manualNavigation: ManualNavigationService
  ) {
    this.isAdminMode = BaseServices.UserRole === 'Admin';
    this.allManualList = this.route.snapshot.data['allManualList'];
  }

  /**
   * Lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * Removes 'opened' class from body and binds the initial manual list.
   */
  ngOnInit(): void {
    document.querySelector('body').classList.remove('opened');
    this.BindManualList(this.manualType);
  }

  /**
   * Prepares and shows the dialog for creating a new manual.
   * Sets the dialog title based on the current manual type.
   */
  newManual() {
    this.manualFormShowSaveAndClose = true;
    this.manualFormHideSaveAndContinue = false;
    this.manual = new ManualView();
    this.showDialog = true;
    if (this.manual.Id === 0) {
      if (this.manualType === 'common') {
        this.newManualText = 'NEW_COMMON_MANUAL';
      } else if (this.manualType === 'portal') {
        this.newManualText = 'NEW_PORTAL_MANUAL';
      } else if (this.manualType === 'business') {
        this.newManualText = 'NEW_BUSINESS_MANUAL';
      } else {
        this.newManualText = 'NEW_COMMON_MANUAL';
      }
    }
  }

  /**
   * Handles the close event emitted from the manual form dialog.
   * Shows a toast message and navigates or refreshes the list if the form was saved.
   * @param returnObj - The object returned from the dialog, containing save status and manual data.
   */
  onClose(returnObj: any) {
    if (returnObj.isSaved) {
      if (returnObj.isProceed) {
        this.router.navigate(
          ['./view', returnObj.manual.Id, returnObj.manual.Title],
          { relativeTo: this.route.parent }
        );
      } else {
        this.showPrimeToast('success', 'Manual', 'Manual saved successfully');
        this.BindUpdatedList();
      }
    }
    this.showDialog = false;
  }

  /**
   * Handles tab click events to switch between different manual types.
   * Updates the manual type and dialog title accordingly.
   * @param manualType - The type of manual selected ('common', 'portal', 'business').
   */
  onTabClick(manualType: string) {
    this.manualType = manualType;
    switch (manualType) {
      case 'common':
        this.newManualText = 'EDIT_COMMON_MANUAL';
        break;
      case 'portal':
        this.newManualText = 'EDIT_PORTAL_MANUAL';
        break;
      case 'business':
        this.newManualText = 'EDIT_BUSINESS_MANUAL';
        break;
    }
    this.changeManualList(this.manualType);
  }

  /**
   * Updates the currently displayed list of manuals based on the selected type.
   * @param type - The type of manual list to display.
   */
  changeManualList(type: any) {
    let manualList: any = [];
    switch (type) {
      case 'common':
        manualList = this.manualList;
        break;
      case 'portal':
        manualList = this.portalManualList;
        break;
      case 'business':
        manualList = this.businessManualList;
        break;
    }
    this.manuals = manualList;
  }

  /**
   * Binds the initial manual lists from the route snapshot data based on the user's role and selected type.
   * @param type - The initial manual type to display.
   */
  BindManualList(type: any) {
    this.manualList = this.route.snapshot.data['manualList'];
    this.manualNavigation.store(this.manualList);
    this.portalManualList = this.route.snapshot.data['portalManualList'];
    this.businessManualList = this.route.snapshot.data['businessManualList'];

    let manualList: any = [];
    switch (type) {
      case 'common':
        manualList = this.manualList;
        break;
      case 'portal':
        manualList = this.portalManualList;
        break;
      case 'business':
        manualList = this.businessManualList;
        break;
    }
    this.manuals = manualList;
  }

  /**
   * Fetches and re-binds the manual lists from the service after an update operation.
   * Tailors the fetch requests based on the user's role.
   */
  BindUpdatedList() {
    this.manualList = [];
    this.portalManualList = [];
    this.businessManualList = [];

    if (BaseServices.UserRole === 'Admin') {
      this.subscriptions.push(
        this.manualService
          .getCommonManuallist(BaseServices.ApplicationId)
          .subscribe(result => {
            this.manualList = result;
            this.manualNavigation.store(result);
            this.changeManualList(this.manualType);
          })
      );
      this.subscriptions.push(
        this.manualService
          .getPortalManuallist(BaseServices.ApplicationId)
          .subscribe(result => {
            this.portalManualList = result;
            this.changeManualList(this.manualType);
          })
      );
      this.subscriptions.push(
        this.manualService
          .getBusinessManuallist(BaseServices.ApplicationId)
          .subscribe(result => {
            this.businessManualList = result;
            this.changeManualList(this.manualType);
          })
      );
    } else if (BaseServices.UserRole === 'Portal') {
      this.subscriptions.push(
        this.manualService
          .getCommonManuallistByPortalLogin(+BaseServices.PortalId)
          .subscribe(result => {
            this.manualList = result;
            this.manualNavigation.store(result);
            this.changeManualList(this.manualType);
          })
      );
    } else if (BaseServices.UserRole === 'Editor') {
      this.subscriptions.push(
        this.manualService
          .getCommonManuallistByBusinessLogin(BaseServices.BusinessId)
          .subscribe(result => {
            this.manualList = result;
            this.manualNavigation.store(result);
            this.changeManualList(this.manualType);
          })
      );
    }
  }

  /**
   * Prepares and shows the dialog for editing an existing manual.
   * Sets the dialog title based on the manual type and whether it's an edit or new operation.
   * @param manual - The manual object to be edited.
   */
  onManualEdit(manual: ManualView) {
    this.manualFormShowSaveAndClose = true;
    this.manualFormHideSaveAndContinue = false;
    this.showDialog = true;
    this.manual = manual;
    switch (this.manualType) {
      case 'common':
        if (this.manual.Id !== 0) {
          this.newManualText = 'EDIT_COMMON_MANUAL';
        } else {
          this.newManualText = 'NEW_COMMON_MANUAL';
        }
        break;
      case 'portal':
        if (this.manual.Id !== 0) {
          this.newManualText = 'EDIT_PORTAL_MANUAL';
        } else {
          this.newManualText = 'NEW_PORTAL_MANUAL';
        }
        break;
      case 'business':
        if (this.manual.Id !== 0) {
          this.newManualText = 'EDIT_BUSINESS_MANUAL';
        } else {
          this.newManualText = 'NEW_BUSINESS_MANUAL';
        }
        break;
    }
  }

  /**
   *
   * @param newState - The new state object {showSaveAndClose: boolean, hideSaveandcontinue: boolean}.
   */
  onManualFormButtonStateChange(newState: {
    showSaveAndClose: boolean;
    hideSaveandcontinue: boolean;
  }) {
    this.manualFormShowSaveAndClose = newState.showSaveAndClose;
    this.manualFormHideSaveAndContinue = newState.hideSaveandcontinue;
    // Ensure Angular detects the change in the parent component's state
    this.cdr.detectChanges();
  }

  /**
   * Handles the deletion of a manual.
   * Calls the appropriate delete service method based on the manual type.
   * @param id - The ID of the manual to be deleted.
   */
  onManualDelete(id: number) {
    const request = this.manualType === 'common'
      ? this.manualService.DeleteAppManualLevel(id)
      : this.manualType === 'portal'
        ? this.manualService.DeletePortalManualLevel(id)
        : this.manualType === 'business'
          ? this.manualService.DeleteBusinessManualLevel(id)
          : null;
    if (!request) return;

    this.subscriptions.push(request.subscribe({
      next: result => {
        if (result !== true) {
          this.messageService.error('MANUAL_ACTION_FAILED');
          return;
        }
        this.BindUpdatedList();
        this.messageService.success('UI_NOTIFICATIONS.DELETED');
      },
      // HTTP failures, including retirement conflicts, are reported once by the interceptor.
      error: () => {}
    }));
  }

  /**
   * Triggers the cancel operation on the manual form component.
   * If the form component is not available, it directly hides the dialog.
   */
  triggerCancelClose() {
    if (this.manualFormComponent) {
      this.manualFormComponent.cancelClose();
    } else {
      this.showDialog = false;
    }
  }

  /**
   * Triggers the save operation on the manual form component.
   * Checks if the form component is available and if the form is valid before proceeding.
   * @param proceed - Optional boolean indicating if the user intends to save and continue.
   */
  triggerSaveManual(proceed?: boolean) {
    if (
      this.manualFormComponent &&
      this.manualFormComponent.createManualForm.valid
    ) {
      this.manualFormComponent.saveManual(proceed);
    } else {
      console.error('Manual form is invalid or not available.');
    }
  }

  /**
   * Shows a PrimeNG toast message.
   * @param severity - The severity level of the toast ('success', 'info', 'warn', 'error').
   * @param summary - The summary text of the toast message.
   * @param detail - The detailed message of the toast.
   */
  showPrimeToast(severity: string, summary: string, detail: string) {
    this.messageService.add({
      severity,
      summary,
      detail,
      life: 3000
    });
  }

  /**
   * Lifecycle hook that is called when a directive, pipe, or service is destroyed.
   * Unsubscribes from all active subscriptions to prevent memory leaks.
   */
  ngOnDestroy() {
    this.subscriptions.forEach((sub, i) => {
      sub.unsubscribe();
    });
  }
}
